<script setup lang="ts">
import { computed, inject, onMounted, ref, watch } from "vue";
import { useConfigurationsStore } from "../../../stores";
import AppModal from "@/components/ui/AppModal.vue";
// @ts-ignore - vuejs-datepicker doesn't have type definitions
import Datepicker from "vuejs-datepicker";
import InsolvencyFormFiles
  from "@/domains/home/services/configurations/views/settings/insolvency/InsolvencyFormFiles.vue";
import WindingInsolvencyDetails
  from "@/domains/home/services/configurations/views/settings/insolvency/WindingInsolvencyDetails.vue";
import { api } from "@/app/services/api.service.ts";

interface Submission {
  trackingNumber: string
  brn: string
  companyName: string
  type: string
  windingUpType?: string
  status: string
  registrarName?: string
  lastDateOfPublication: string
  dateOfConfirmation: string
  dateOfDissolution: string
}

interface Pagination {
  totalElements: number
  totalPages: number
}

interface Attachment {
  id: string
  dateOfPublication: string
}

interface Application {
  attachments: Attachment[]
  type: string
  trackingNumber: string
}

interface UpdateForm {
  attachmentId: string | null
  newDate: string | null
  updateDateOfDissolution: boolean
}

const store = useConfigurationsStore();
const canFunc = inject<any>('$can')
const showConfirmDialog = inject<any>('showConfirmDialog')
const successToast = inject<any>('successToast')
const errorToast = inject<any>('errorToast')
const dateFormat = inject<any>('dateFormat')
const cleanUnderscores = inject<any>('cleanUnderscores')

const showApplicationDetails = ref(false)
const showUpdateDatePopup = ref(false)
const loading = ref(false)
const currentPage = ref(1)
const limit = ref(12)
const submissions = ref<Submission[]>([])
const pagination = ref<Pagination>({
  totalElements: 0,
  totalPages: 0
})
const statuses = ref('APPROVED')
const search = ref('')
const application = ref<Application | null>(null)
const hasError = ref(false)
const errorMessage = ref<string | null>(null)
const loadingIndex = ref<number | null>(null)
const abortLoadingIndex = ref<number | null>(null)
const trackingNumber = ref<string | null>(null)

const updateForm = ref<UpdateForm>({
  attachmentId: null,
  newDate: null,
  updateDateOfDissolution: false
})

const rows = {
  odd: 'border-t border-gray-200 hover:bg-gray-10',
  even: 'border-t border-gray-200 bg-gray-10'
}

const insolvencySubmissions = computed(() => store.insolvencies);
const insolvencyError = computed(() => store.insolvencyError);

const fetch = () => {
  const data = {
    status: statuses.value,
    search: search.value,
    page: currentPage.value,
    pageSize: limit.value,
    type: 'WINDING_UP,FOREIGN_COMPANY_DE_REGISTRATION'
  }
  loading.value = true
  const url = Object.entries(data)
    .filter(([, value]) => value !== null && value !== '')
    .map(([key, value]) => `${key}=${value}`)
    .join('&')

  store.fetchAllInsolvency(`?${url}`);
}

const showDetails = (trackingNo: string) => {
  trackingNumber.value = trackingNo
  showApplicationDetails.value = true
}

const next = () => {
  if (currentPage.value < pagination.value.totalPages) {
    currentPage.value++
    fetch()
  }
}

const previous = () => {
  if (currentPage.value > 1) {
    currentPage.value--
    fetch()
  }
}

const abortApplication = async (trackingNo: string, idx: number) => {
  const confirmed = await showConfirmDialog?.('Are you sure that you want to roll back this application?')
  if (confirmed) {
    loading.value = true
    abortLoadingIndex.value = idx
    try {
      await api().post(`/insolvency/process/rollback/${trackingNo}`)
      successToast?.('Success', 'Application rolled back successfully')
      fetch()
    } catch (error: any) {
      errorToast?.('Error', error.response?.data?.message || 'An error occurred')
    } finally {
      abortLoadingIndex.value = null
    }
  }
}

const fetchInsolvency = async (app: Submission) => {
  const trackingNo = app.trackingNumber
  let path = `insolvency/winding-up/details?tracking-number=${trackingNo}`
  if (app.type === 'FOREIGN_COMPANY_DE_REGISTRATION') {
    path = `insolvency/process/by-tracking-no?trackingNumber=${trackingNo}`
  }

  try {
    const response = await api().get(path)
    application.value = response.data.data
    showUpdateDatePopup.value = true
  } catch (error) {
    // Handle error
  } finally {
    loadingIndex.value = null
  }
}

const showUpdateForm = (app: Submission, idx: number) => {
  loadingIndex.value = idx
  fetchInsolvency(app)
}

const updateDateOfPublication = async () => {
  loading.value = true
  updateForm.value.newDate = dateFormat(updateForm.value.newDate);

  try {
    await api().post('/insolvency/process/fix-last-date-of-publication', updateForm.value)
    successToast?.('Success', 'Successfully updated date of publication')
    window.location.reload()
  } catch (error) {
    errorToast?.('Error', 'Failed to update date of publication')
  } finally {
    loading.value = false
  }
}

const setAttachmentId = (attachment: Attachment) => {
  updateForm.value.attachmentId = attachment.id
  updateForm.value.newDate = attachment.dateOfPublication
}

const canViewMenuItem = (permission: string): boolean => {
  return canFunc?.(permission, 'general') === true
}

onMounted(() => {
  fetch()
})

watch(insolvencySubmissions, (data) => {
  loading.value = false
  if (data) {
    submissions.value = data.content
    pagination.value = {
      totalPages: data.totalPages,
      totalElements: data.totalElements
    }
  }
})

watch(insolvencyError, (data) => {
  loading.value = false
  if (data) {
    errorToast?.('Error', data.messages)
  }
})

watch(limit, () => {
  currentPage.value = 1
  fetch()
})
</script>

<template>
  <div class="flex flex-col">
    <!-- Modals -->
    <AppModal v-model="showApplicationDetails" title="Application Details" size="xl" hide-footer>
      <WindingInsolvencyDetails :tracking-number="trackingNumber || ''" />
    </AppModal>

    <AppModal v-model="showUpdateDatePopup" title="Update date of publication" hide-footer size="xl">
      <div class="grid grid-cols-12 gap-4 p-4">
        <div class="col-span-6">
          <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">New date of publication</label>
          <datepicker
            :disabled-dates="{ from: new Date() }"
            class="noFocus text-sm"
            :typeable="false"
            bootstrap-styling
            format="dd/MM/yyyy"
            placeholder="dd/mm/yyyy"
            v-model="updateForm.newDate"
          />
        </div>

        <InsolvencyFormFiles
          v-if="application && application.attachments.length > 0"
          @attachment="setAttachmentId"
          :task="application.attachments"
        />

        <div class="col-span-12 flex justify-end mt-4">
          <button
            @click="updateDateOfPublication"
            class="btn-primary"
            type="button"
          >
            Update
          </button>
        </div>
      </div>
    </AppModal>

    <!-- Filters -->
    <div class="grid grid-cols-4 gap-4 mb-4">
        <input
          type="text"
          v-model="search"
          placeholder="Search by BRN, TrackingNo"
          @keyup.enter="fetch"
          class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
        />
        <select
          v-model="statuses"
          class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary">
          <option value="APPROVED">Approved</option>
        </select>
      <div class="col-span-1">
        <select v-model="limit"
                class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary">
          <option value="12">12</option>
          <option value="20">20</option>
          <option value="50">50</option>
          <option value="100">100</option>
        </select>
      </div>
      <span class="text-center text-xs font-bold my-auto">
        <span>{{ ((currentPage - 1) * limit) + 1 }}</span> -
        <span v-if="limit > submissions.length">{{ pagination.totalElements }}</span>
        <span v-else>{{ currentPage * limit }}</span>
        of {{ pagination.totalElements }}
      </span>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="w-full bg-gray-200 rounded-full h-1 overflow-hidden mb-4">
      <div class="bg-primary h-1 rounded-full animate-pulse" style="width: 100%"></div>
    </div>

    <!-- Table -->
    <div class="bg-white rounded border border-gray-200 overflow-hidden flex-1">
      <div class="overflow-x-auto">
        <table class="w-full text-xs">
          <thead class="bg-gray-50 sticky top-0">
            <tr>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">#</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Tracking No.</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">BRN & Name</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Type</th>
              <th class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Status</th>
              <th class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Assignment</th>
              <th class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Last Date Of Publication</th>
              <th class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Date Of Confirmation</th>
              <th class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Dissolution Date</th>
              <th class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(submission, idx) in submissions"
              :key="idx"
              :class="[(idx % 2) ? rows.even : rows.odd]"
            >
              <td class="py-3 px-4 border-b border-gray-200">{{ (currentPage - 1) * limit + idx + 1 }}</td>
              <td
                class="py-3 px-4 border-b border-gray-200 text-primary font-bold cursor-pointer hover:underline"
                @click="showDetails(submission.trackingNumber)"
              >
                {{ submission.trackingNumber }}
              </td>
              <td class="py-3 px-4 border-b border-gray-200 text-black font-bold">
                {{ submission.brn }}<br />{{ submission.companyName }}
              </td>
              <td class="py-3 px-4 border-b border-gray-200 font-bold">
                {{ cleanUnderscores(submission.type) }}
                <span v-if="submission.type === 'WINDING_UP'">[{{ submission.windingUpType }}]</span>
              </td>
              <td class="py-3 px-4 border-b border-gray-200">
                <p class="text-xs px-2 py-1 text-capitalize text-center bg-gray-50 rounded">
                  <span
                    :class="{
                      'text-gray-400': submission.status.toUpperCase() === 'DRAFT',
                      'text-red': submission.status.toUpperCase() === 'PAYMENT_FAILED' || submission.status.toUpperCase() === 'REJECTED',
                      'text-purple': submission.status.toUpperCase() === 'AWAITING_PAYMENT',
                      'text-gray-600': submission.status.toUpperCase() === 'SUBMITTED',
                      'text-primary': submission.status.toUpperCase() === 'ASSIGNED',
                      'text-green': submission.status.toUpperCase() === 'APPROVED',
                      'text-orange': submission.status.toUpperCase() === 'QUERIED'
                    }"
                  >
                    <span v-if="submission.status.toUpperCase() === 'AWAITING_PAYMENT'">Confirming Payment</span>
                    <span v-else-if="submission.status.toUpperCase() === 'SUBMITTED'">In Queue</span>
                    <span v-else>{{ submission.status.toLowerCase().replaceAll('_', ' ') }}</span>
                  </span>
                </p>
              </td>
              <td class="py-3 px-4 border-b border-gray-200 text-center text-capitalize">
                <span v-if="submission.registrarName" class="text-xs">{{ submission.registrarName.toLowerCase() }}</span>
                <span v-else class="text-xs">Not Assigned</span>
              </td>
              <td class="py-3 px-4 border-b border-gray-200 text-center">
                {{ dateFormat(submission.lastDateOfPublication) }}
                <button
                  @click="showUpdateForm(submission, idx)"
                  type="button"
                  class="btn-sm-primary mt-1"
                >
                  <i class="fa-solid fa-pencil"></i> Edit
                </button>
              </td>
              <td class="py-3 px-4 border-b border-gray-200 text-center">
                {{ dateFormat(submission.dateOfConfirmation) }}
              </td>
              <td class="py-3 px-4 border-b border-gray-200 text-center">
                {{ dateFormat(submission.dateOfDissolution) }}
              </td>
              <td class="py-3 px-4 border-b border-gray-200 text-center">
                <button
                  v-if="canViewMenuItem('can_roll_back_insolvency_application')"
                  @click="abortApplication(submission.trackingNumber, idx)"
                  type="button"
                  class="btn-sm-danger">
                  <i class="fa-solid fa-undo"></i> Rollback
                </button>
              </td>
            </tr>
            <tr v-if="submissions.length === 0">
              <td colspan="10" class="bg-gray-50 text-center py-4">
                <span class="text-sm">No results found</span>
              </td>
            </tr>
            <tr v-if="hasError">
              <td colspan="10" class="bg-red text-center py-4 text-white">
                <p class="text-sm">Failed to load tasks. Try again later.</p>
                <p class="text-sm">{{ errorMessage }}</p>
                <p class="text-sm">
                  <button class="btn-primary bg-white text-red px-2 rounded mt-2" @click="fetch">Reload</button>
                </p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="pagination.totalPages > 1"
         class="flex items-center justify-between px-1 py-3 border-t border-gray-100 mt-2">
      <p class="text-xs text-gray-500">Page <span class="font-semibold text-gray-700">{{ currentPage }}</span> of <span
        class="font-semibold text-gray-700">{{ pagination.totalPages }}</span></p>
      <div class="flex gap-2">
        <button class="btn-sm-primary" @click="previous" :disabled="currentPage === 1">
          <i class="fa fa-chevron-left mr-1"></i> Prev
        </button>
        <button class="btn-sm-primary" @click="next" :disabled="currentPage === pagination.totalPages">
          Next <i class="fa fa-chevron-right ml-1"></i>
        </button>
      </div>
    </div>
  </div>
</template>
